import type {LibraryEntry} from './library';
import {conversationParent} from './conversation-presentation';

export interface ConversationTreeNode {entry:LibraryEntry;children:ConversationTreeNode[]}
export interface ConversationTree {roots:ConversationTreeNode[];nodes:Map<string,ConversationTreeNode>;parents:Map<string,string>}

/** Exact source relationships only; catalog membership and source lineage remain unchanged. */
export function conversationTree(entries:LibraryEntry[]):ConversationTree {
  const nodes=new Map<string,ConversationTreeNode>(),parents=new Map<string,string>();
  for(const entry of entries)if(entry.kind==='thread'&&!nodes.has(entry.id))nodes.set(entry.id,{entry,children:[]});
  for(const [id,node] of nodes){
    const source=node.entry.kind==='thread'?conversationParent(node.entry.thread):undefined;
    if(source&&source!==id&&nodes.has(source))parents.set(id,source);
  }
  // Corrupt cycles never hide conversations or invent an arbitrary source relation.
  const visited=new Set<string>();
  for(const id of nodes.keys()){
    const path:string[]=[],indices=new Map<string,number>();let cursor:string|undefined=id;
    while(cursor&&!visited.has(cursor)&&!indices.has(cursor)){
      indices.set(cursor,path.length);path.push(cursor);cursor=parents.get(cursor);
    }
    if(cursor&&indices.has(cursor))for(const cyclic of path.slice(indices.get(cursor)!))parents.delete(cyclic);
    for(const seen of path)visited.add(seen);
  }
  const roots:ConversationTreeNode[]=[],emitted=new Set<string>();
  for(const entry of entries){
    if(entry.kind!=='thread'){roots.push({entry,children:[]});continue;}
    if(emitted.has(entry.id))continue;emitted.add(entry.id);
    const node=nodes.get(entry.id)!,parent=parents.get(entry.id);
    if(parent)nodes.get(parent)!.children.push(node);else roots.push(node);
  }
  return {roots,nodes,parents};
}

/** Directory members anchor the view; their verified descendants remain visible across folders. */
export function conversationDirectoryTree(entries:LibraryEntry[],relatedEntries:LibraryEntry[]):ConversationTree {
  const complete=conversationTree(relatedEntries),anchors=new Set(entries.filter(entry=>entry.kind==='thread').map(entry=>entry.id));
  const roots:ConversationTreeNode[]=[],nodes=new Map<string,ConversationTreeNode>(),parents=new Map<string,string>();
  for(const entry of entries){
    if(entry.kind!=='thread'){roots.push({entry,children:[]});continue;}
    if(nodes.has(entry.id)||conversationAncestors(complete,entry.id).some(id=>anchors.has(id)))continue;
    const root=complete.nodes.get(entry.id);if(!root)continue;
    roots.push(root);
    const pending=[root];
    while(pending.length){
      const node=pending.pop()!;nodes.set(node.entry.id,node);
      for(const child of node.children){parents.set(child.entry.id,node.entry.id);pending.push(child);}
    }
  }
  return {roots,nodes,parents};
}

export function conversationAncestors(tree:ConversationTree,id:string):string[]{
  const result:string[]=[];let parent=tree.parents.get(id);
  while(parent){result.unshift(parent);parent=tree.parents.get(parent);}
  return result;
}

export function conversationExpansion(value:unknown):Set<string>{
  return new Set(Array.isArray(value)?value.filter((id):id is string=>typeof id==='string'):[]);
}

/** Reveal only newly observed, exact quick-chat parent paths; refreshes respect manual folding. */
export function newQuickConversationAncestors(tree:ConversationTree,known:ReadonlySet<string>):{known:Set<string>;ancestors:Set<string>}{
 const next=new Set<string>(),ancestors=new Set<string>();
 for(const [id,node] of tree.nodes){
  if(node.entry.kind!=='thread'||!node.entry.thread.quickChat)continue;
  const key=JSON.stringify([id,node.entry.thread.quickChat.parentThreadId??null]);next.add(key);
  if(!known.has(key))for(const parent of conversationAncestors(tree,id))ancestors.add(parent);
 }
 return {known:next,ancestors};
}
